import type * as t from "./types";

import QuizIntro from "./QuizIntro.svelte";
import { getHostData } from "./utils";

function createIntroStep(block: t.IntroBlock): t.IntroStep {
    return {
        type: "intro",
        logo: block.settings.logo,
        maxLogoSize: block.settings.max_logo_size,
        title: block.settings.title,
        subtitle: block.settings.subtitle,
        footer: block.settings.footer,
        ctaButton: {
            text: block.settings.cta_button_text,
            backgroundColor: block.settings.cta_button_bg_color,
            foregroundColor: block.settings.cta_button_fg_color,
            border: {
                color: block.settings.cta_button_border_color,
                width: block.settings.cta_button_border_width,
                radius: block.settings.cta_button_border_radius,
            },
        },
    };
}

class QuizIntroElement extends HTMLElement {
    private intro?: QuizIntro;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if (this.intro != null) {
            return;
        }

        if (this.shadowRoot == null) {
            console.error(
                `[shpln-quiz][style-wrapper] Shadow root is unexpectedly null`,
            );
            return;
        }

        const instanceId = this.dataset.instanceId;
        if (instanceId == null) {
            throw new Error(
                "[shpln-quiz] Instance id not found in <shpln-quiz> dataset.",
            );
        }

        const { props, cssSources } = getHostData(instanceId);

        this.setupCss(cssSources);

        const quiz = document.querySelector("shpln-quiz");
        if (quiz == null) {
            throw new Error("[shpln-quiz] Quiz element is not instantiated");
        }

        const introStep = createIntroStep(props.blocks[0]);
        if (introStep?.type !== "intro") {
            throw new Error("[shpln-quiz-intro] Intro step not found.");
        }

        this.intro = new QuizIntro({
            target: this.shadowRoot,
            props: {
                step: introStep,
                host: this,
            },
        });
    }

    private setupCss(sources: string[] | undefined) {
        if (sources == null) {
            throw new Error("[shpln-quiz] css sources cannot be found.");
        } else if (sources.length === 0) {
            throw new Error(
                `[shpln-quiz][style-wrapper] css sources list is empty`,
            );
        }

        const linkTags: HTMLLinkElement[] = [];
        // Loop backwards because we are prepending each item. This way we can
        // preserve the order of CSS imports.
        for (let i = sources.length - 1; i >= 0; i--) {
            const source = sources[i];
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = source;
            this.shadowRoot?.prepend(link);
            linkTags.push(link);
        }

        // Listen to all link tags 'load' events. When all are loaded, an event
        // is emitted.
        let semaphore = linkTags.length;
        for (const linkTag of linkTags) {
            linkTag.addEventListener("load", () => {
                --semaphore;
                if (semaphore === 0) {
                    this.handleCssLoaded();
                }
            });
        }
    }

    private handleCssLoaded() {
        this.dispatchEvent(new CustomEvent("css-ready"));
    }
}

// Define the custom element
customElements.define("shpln-quiz-intro", QuizIntroElement);
